/**
 * Type scale.
 *
 * Inter, self-hosted through `@fontsource/inter` - not a Google Fonts link,
 * because the panel is an internal tool that should render the same on a
 * connection that cannot reach fonts.googleapis.com.
 *
 * The scale is deliberately short: five display sizes and two body sizes. The
 * old theme declared only a font family, so every heading fell back to MUI's
 * defaults and screens set their own sizes inline, which is how one page ended
 * up with a 1.5rem "title" and the next with a 24px one.
 *
 * `menuCaption`, `subMenuCaption`, `commonAvatar` and `mediumAvatar` are
 * spread by the layout components (`theme.typography.menuCaption`). They were
 * *read* by the old code but never *defined* by the old theme, so those rules
 * silently resolved to `undefined`.
 */

const FONT_STACK = [
  "Inter",
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  "Helvetica",
  "Arial",
  "sans-serif",
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
].join(",");

const typography = {
  fontFamily: FONT_STACK,

  h1: { fontSize: "2rem", fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.02em" },
  h2: { fontSize: "1.75rem", fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.02em" },
  h3: { fontSize: "1.5rem", fontWeight: 700, lineHeight: 1.3, letterSpacing: "-0.015em" },
  h4: { fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.35, letterSpacing: "-0.01em" },
  h5: { fontSize: "1.0625rem", fontWeight: 600, lineHeight: 1.4 },
  h6: { fontSize: "0.9375rem", fontWeight: 600, lineHeight: 1.45 },

  subtitle1: { fontSize: "0.9375rem", fontWeight: 500, lineHeight: 1.5 },
  subtitle2: { fontSize: "0.875rem", fontWeight: 500, lineHeight: 1.5 },

  body1: { fontSize: "0.875rem", fontWeight: 400, lineHeight: 1.55 },
  body2: { fontSize: "0.8125rem", fontWeight: 400, lineHeight: 1.55 },

  caption: { fontSize: "0.75rem", fontWeight: 400, lineHeight: 1.5 },
  overline: {
    fontSize: "0.6875rem",
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
  },

  // Buttons do not shout. MUI uppercases button labels by default, which is
  // what made "ADD NEW MOVIE" and "FILTER" read as warnings.
  button: {
    fontSize: "0.875rem",
    fontWeight: 600,
    lineHeight: 1.4,
    textTransform: "none" as const,
  },

  /** Sidebar group heading ("Content Management"). */
  menuCaption: {
    fontSize: "0.6875rem",
    fontWeight: 600,
    color: "#98a2b3",
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
    padding: "18px 12px 6px",
  },

  /** The line under a sidebar group heading, where a group carries one. */
  subMenuCaption: {
    fontSize: "0.6875rem",
    fontWeight: 400,
    color: "#98a2b3",
    textTransform: "none" as const,
  },

  commonAvatar: {
    cursor: "pointer",
    borderRadius: "8px",
  },

  mediumAvatar: {
    width: "34px",
    height: "34px",
    fontSize: "0.875rem",
    fontWeight: 600,
  },
};

export default typography;
